import { Component } from '@theme/component';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const selectors = {
  DATA_SINGLE_NAME: 'data-single-name',
  NAME_ATTRIBUTE: 'name',
};

const attributes = {
  DATA_OPEN: 'data-open',
  DURATION: 300,
};

class AccordionCustom extends Component {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary') || null;
    this.content = /** @type {HTMLElement | null} */ (this.summary?.nextElementSibling || null);
    this.animation = null;
    this.closing = false;
    this.expanding = false;
    /** @type {string | null} */
    this.originalName = null;
    this.handleMediaChange = this.defaultState.bind(this);
  }

  connectedCallback() {
    if (!this.details || !this.summary || !this.content) return;

    this.defaultState();
    this.summary.addEventListener('click', this.handleClick);
    mediaQueryLarge.addEventListener('change', this.handleMediaChange);
  }

  disconnectedCallback() {
    if (!this.summary) return;

    this.summary.removeEventListener('click', this.handleClick);
    mediaQueryLarge.removeEventListener('change', this.handleMediaChange);

    if (this.animation) {
      this.animation.cancel();
      this.animation = null;
    }
  }

  defaultState() {
    if (!this.details) return;

    const { disabled } = this.details.dataset;

    if (!disabled) return;

    const isMobile = isMobileBreakpoint();

    this.originalName ??= this.details.getAttribute(selectors.NAME_ATTRIBUTE);

    if ((disabled === 'mobile' && isMobile) || (disabled === 'desktop' && !isMobile)) {
      this.openAccordion(0);
      this.details.removeAttribute(selectors.NAME_ATTRIBUTE);
    } else {
      this.shrink(0);

      if (this.originalName) {
        this.details.setAttribute(selectors.NAME_ATTRIBUTE, this.originalName);
      } else {
        this.details.removeAttribute(selectors.NAME_ATTRIBUTE);
      }
    }
  }

  handleClick = (/** @type {MouseEvent} */ event) => {
    if (!this.details) return;

    event.preventDefault();

    const name = this.details.getAttribute(selectors.NAME_ATTRIBUTE);
    const singleContainer = this.closest(`[${selectors.DATA_SINGLE_NAME}]`);

    let singleGroup = null;

    if (singleContainer) {
      try {
        // Scope to direct accordion children to avoid affecting nested accordion groups.
        singleGroup = singleContainer.querySelectorAll(':scope > accordion-custom > details');
      } catch (_error) {
        // Fallback for environments that do not support :scope in querySelectorAll.
        console.error('Error selecting accordion group:', _error);
        singleGroup = singleContainer.querySelectorAll('accordion-custom > details');
      }
    }

    const group = singleGroup
      ? singleGroup
      : name
        ? this.parentElement?.querySelectorAll(`accordion-custom > details[name="${name}"]`)
        : null;

    (group || []).forEach((item) => {
      const detailsItem = /** @type {HTMLDetailsElement} */ (item);
      const parentAccordion = /** @type {(HTMLElement & { shrink?: () => void }) | null} */ (
        detailsItem.parentElement
      );

      if (
        parentAccordion !== this &&
        detailsItem.open &&
        typeof parentAccordion?.['shrink'] === 'function'
      ) {
        parentAccordion['shrink']();
      }
    });

    if (this.closing || !this.details.open) {
      this.openAccordion();
    } else if (this.expanding || this.details.open) {
      this.shrink();
    }
  };

  shrink(duration = attributes.DURATION) {
    if (!this.details || !this.summary) return;

    // Cancel in-flight animations to prevent state races when users click quickly.
    if (this.animation) {
      this.animation.cancel();
    }

    this.details.removeAttribute(attributes.DATA_OPEN);
    this.closing = true;

    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    this.animation = this.details.animate({ height: [startHeight, endHeight] }, { duration });
    this.animation.onfinish = () => this.finishAnimation(false);
    this.animation.oncancel = () => (this.closing = false);
  }

  openAccordion(duration = attributes.DURATION) {
    if (!this.details) return;

    this.details.toggleAttribute(attributes.DATA_OPEN);
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;

    window.requestAnimationFrame(() => this.expand(duration));
  }

  expand(duration = attributes.DURATION) {
    if (!this.details || !this.summary || !this.content) return;

    // Cancel in-flight animations to prevent state races when users click quickly.
    if (this.animation) {
      this.animation.cancel();
    }

    this.expanding = true;

    const clone = document.body.appendChild(document.createElement('div'));

    clone.style.cssText = 'position:absolute;visibility:hidden';
    clone.innerHTML = this.details.outerHTML;

    const content = /** @type {HTMLElement | null} */ (
      clone.querySelector('summary')?.nextElementSibling || null
    );
    const marginTop = content ? parseFloat(getComputedStyle(content).marginTop) : 0;
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight + marginTop}px`;

    this.animation = this.details.animate({ height: [startHeight, endHeight] }, { duration });
    this.animation.onfinish = () => this.finishAnimation(true);
    this.animation.oncancel = () => (this.expanding = false);

    document.body.removeChild(clone);
  }

  finishAnimation(/** @type {boolean} */ open) {
    if (!this.details) return;

    this.details.open = open;
    this.animation = null;
    this.closing = false;
    this.expanding = false;
    this.details.style.height = '';
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
